/** 议价单自动同意/拒绝规则（按店铺存 preferences） */

import { latestCostHistory } from '@/shein-platform/modules/discuss/discussDisplay';

export const DISCUSS_AUTO_RULES_PREF_KEY = 'discussAutoRulesByShop';

export function makeDiscussAutoRuleId() {
  return `dar_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function normalizeDiscussAutoRule(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const categoryId = String(raw.categoryId ?? '').trim();
  if (!categoryId) return null;
  const rejectBelowPercent = Number(raw.rejectBelowPercent);
  const acceptAbovePercent = Number(raw.acceptAbovePercent);
  if (!Number.isFinite(rejectBelowPercent) || !Number.isFinite(acceptAbovePercent)) return null;
  return {
    id: String(raw.id || makeDiscussAutoRuleId()),
    enabled: raw.enabled !== false,
    categoryId,
    categoryPathLabel: String(raw.categoryPathLabel || '').trim(),
    productTypeId: raw.productTypeId != null ? String(raw.productTypeId) : '',
    rejectBelowPercent: Math.min(100, Math.max(0, rejectBelowPercent)),
    acceptAbovePercent: Math.min(100, Math.max(0, acceptAbovePercent)),
  };
}

export function readDiscussAutoRulesFromPreferences(preferences, shopId) {
  const sid = String(shopId || '').trim();
  if (!sid) return [];
  const root = preferences && typeof preferences === 'object' ? preferences : {};
  const byShop = root[DISCUSS_AUTO_RULES_PREF_KEY];
  const list = byShop && typeof byShop === 'object'
    ? (Array.isArray(byShop[sid]) ? byShop[sid] : [])
    : [];
  return list.map(normalizeDiscussAutoRule).filter(Boolean);
}

export function buildDiscussAutoRulesPreferencePatch(shopId, rules) {
  const sid = String(shopId || '').trim();
  const normalized = (Array.isArray(rules) ? rules : [])
    .map(normalizeDiscussAutoRule)
    .filter(Boolean);
  return {
    [DISCUSS_AUTO_RULES_PREF_KEY]: {
      [sid]: normalized,
    },
  };
}

/**
 * 平台建议价相对商家最新报价的比例（%）。
 * 例：报价 20、建议 10 → 50。
 */
export function discussSuggestRatioPercent(sku) {
  const suggest = Number(sku?.suggestCostPrice);
  const last = latestCostHistory(sku);
  const quote = Number(
    last?.costPrice != null && last.costPrice !== ''
      ? last.costPrice
      : sku?.latestCostPrice,
  );
  if (!Number.isFinite(suggest) || !Number.isFinite(quote) || quote <= 0) {
    return null;
  }
  return (suggest / quote) * 100;
}

/** 单行议价：用最后一个 SKU 的比例做判断（与列表展示一致） */
export function discussRowSuggestRatioPercent(row) {
  const skus = Array.isArray(row?.skuCostPrices) ? row.skuCostPrices : [];
  if (!skus.length) return null;
  return discussSuggestRatioPercent(skus[skus.length - 1]);
}

/**
 * @returns {'accept'|'reject'|null}
 * 低于 rejectBelow → reject；不低于 acceptAbove → accept；中间人工处理。
 */
export function evaluateDiscussAutoAction(row, rules, categoryId = '') {
  const list = (Array.isArray(rules) ? rules : []).filter((r) => r && r.enabled !== false);
  if (!list.length) return null;
  const ratio = discussRowSuggestRatioPercent(row);
  if (ratio == null) return null;

  const cat = String(categoryId || row?.categoryId || row?.category_id || '').trim();
  const matched = list.filter((r) => {
    const rid = String(r.categoryId || '').trim();
    if (rid === '*' || rid === 'all') return true;
    return cat && rid === cat;
  });
  if (!matched.length) return null;

  // 多条命中时取更严：先看是否该拒绝，再看是否该同意
  let shouldReject = false;
  let shouldAccept = false;
  for (const rule of matched) {
    if (ratio < Number(rule.rejectBelowPercent)) shouldReject = true;
    if (ratio >= Number(rule.acceptAbovePercent)) shouldAccept = true;
  }
  if (shouldReject && shouldAccept) {
    // 阈值交叉时优先拒绝（更保守）
    return 'reject';
  }
  if (shouldReject) return 'reject';
  if (shouldAccept) return 'accept';
  return null;
}
